import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getBlogPost } from '../api';
import { marked } from 'marked';

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    getBlogPost(slug).then(setPost).catch(() => setError(true));
  }, [slug]);

  if (error) return (
    <div className="pt-28 pb-20 text-center">
      <h1 className="text-2xl font-bold mb-4">Post Not Found</h1>
      <Link to="/blog" className="text-blue-600 hover:underline">View all posts</Link>
    </div>
  );

  if (!post) return (
    <div className="pt-28 pb-20 text-center">
      <div className="animate-pulse text-gray-400">Loading...</div>
    </div>
  );

  return (
    <div className="pt-20 pb-20">
      {/* Hero with cover image overlay */}
      {post.cover_image && (
        <div className="relative w-full h-96 md:h-[28rem] overflow-hidden">
          <img src={post.cover_image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 text-white max-w-4xl mx-auto">
            {post.category && (
              <span className="text-xs font-semibold uppercase tracking-wider text-blue-300">{post.category}</span>
            )}
            <h1 className="text-3xl md:text-5xl font-bold mt-2 mb-3">{post.title}</h1>
            {post.published_at && (
              <div className="text-gray-300">
                {post.author_name && <><span className="font-medium text-white">{post.author_name}</span>{post.author_role && <>, {post.author_role}</>} · </>}
                {new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            )}
          </div>
        </div>
      )}

      <article className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <Link to="/blog" className="text-blue-600 hover:text-blue-800 text-sm mt-6 mb-6 inline-block">&larr; All Posts</Link>

          {/* Fallback: show title/meta if no cover image */}
          {!post.cover_image && (
            <>
              {post.category && (
                <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider">{post.category}</span>
              )}
              <h1 className="text-3xl md:text-5xl font-bold mt-2 mb-4">{post.title}</h1>
              {post.published_at && (
                <div className="text-gray-500 mb-8">
                  {post.author_name && <><span className="font-medium text-gray-700">{post.author_name}</span>{post.author_role && <>, {post.author_role}</>} · </>}
                  {new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
              )}
            </>
          )}

          {post.body && (
            <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: marked(post.body) }} />
          )}

          {(post.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-8 pt-8 border-t">
              {post.tags.map(t => (
                <span key={t} className="bg-gray-100 text-gray-600 text-sm px-3 py-1 rounded-full">{t}</span>
              ))}
            </div>
          )}
        </div>
      </article>
    </div>
  );
}
